import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Download, PieChart as PieIcon, BarChart3 } from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";

export default function Reports() {
  const { base } = useCurrency();
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);

  const load = useCallback(async () => {
    const [s, t] = await Promise.all([api.get(`/summary?base=${base}`), api.get("/transactions")]);
    setSummary(s.data);
    setTxs(t.data);
  }, [base]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    const header = ["Tarih", "Tür", "Durum", "Tutar", "Para Birimi", "Müşteri", "Açıklama"].join(",");
    const rows = txs.map((t) => [
      t.date,
      t.type === "income" ? "Gelir" : "Gider",
      t.status === "completed" ? "Gerçekleşen" : "Yaklaşan",
      t.amount,
      t.currency,
      `"${(t.counterparty || "").replace(/"/g, '""')}"`,
      `"${(t.description || "").replace(/"/g, '""')}"`,
    ].join(","));
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `finakis-islemler-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalExpense = summary?.expense_completed || 0;

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">Analiz</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Finansal Raporlar</h1>
          <p className="text-sm text-slate-400 mt-1">Kategori dağılımları, aylık trendler ve dışa aktarma.</p>
        </div>
        <Button onClick={exportCSV} data-testid="export-csv-button" className="bg-[#131924] hover:bg-white/5 border border-white/10">
          <Download className="w-4 h-4 mr-1.5" /> CSV Dışa Aktar
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[#131924] border border-white/5 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Dağılım</div>
              <h2 className="font-display text-lg font-bold tracking-tight mt-1">Kategori Bazlı Giderler</h2>
            </div>
            <PieIcon className="w-4 h-4 text-indigo-400" />
          </div>
          {summary?.categories && summary.categories.length > 0 ? (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={summary.categories}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={60}
                    paddingAngle={2}
                  >
                    {summary.categories.map((e, i) => (
                      <Cell key={i} fill={e.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#0F1522", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10 }}
                    formatter={(v) => formatMoney(v, base)}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-slate-500 py-20 text-center">Henüz gider yok.</div>
          )}
        </div>

        <div className="bg-[#131924] border border-white/5 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Trend</div>
              <h2 className="font-display text-lg font-bold tracking-tight mt-1">Aylık Gelir vs Gider</h2>
            </div>
            <BarChart3 className="w-4 h-4 text-indigo-400" />
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={summary?.monthly || []} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="label" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: "#0F1522", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10 }}
                  formatter={(v) => formatMoney(v, base)}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="income" fill="#10B981" name="Gelir" radius={[6, 6, 0, 0]} />
                <Bar dataKey="expense" fill="#F43F5E" name="Gider" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-[#131924] border border-white/5 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Zaman Serisi</div>
            <h2 className="font-display text-lg font-bold tracking-tight mt-1">Net Nakit Trendi (Aylık)</h2>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={summary?.monthly || []} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="label" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: "#0F1522", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10 }}
                formatter={(v) => formatMoney(v, base)}
              />
              <Line type="monotone" dataKey="net" stroke="#6366F1" strokeWidth={3} dot={{ fill: "#6366F1", r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category table */}
      <div className="bg-[#131924] border border-white/5 rounded-xl p-5">
        <h2 className="font-display text-lg font-bold tracking-tight mb-4">Kategori Detayı</h2>
        {summary?.categories && summary.categories.length > 0 ? (
          <div className="space-y-2">
            {summary.categories.map((c) => {
              const pct = totalExpense > 0 ? (c.value / totalExpense) * 100 : 0;
              return (
                <div key={c.name} className="flex items-center gap-3" data-testid={`cat-row-${c.name}`}>
                  <div className="w-32 flex items-center gap-2 text-sm">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />
                    <span className="truncate">{c.name}</span>
                  </div>
                  <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: c.color }} />
                  </div>
                  <div className="font-mono-num text-sm w-28 text-right">{formatMoney(c.value, base)}</div>
                  <div className="font-mono-num text-xs text-slate-500 w-14 text-right">%{pct.toFixed(1)}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-sm text-slate-500 py-6 text-center">Veri yok.</div>
        )}
      </div>
    </div>
  );
}
